import { DataRole, DataType } from '@mode-switch/express';
import { Attr, Names, Values } from '@mode-switch/model';
import {
  DomainResult,
  PivotResult,
  PlanType,
  QueryResult,
  RecordsResult,
  SelectPlan,
  SelectResult,
} from '@mode-switch/query';
import {
  EncField,
  EncFilterField,
  EncSeries,
  Encoding,
  FieldType,
  VariableType,
  Model as VegasModel,
  ViewFieldTypes,
} from '@mode-vegas/vegas';
import * as ChartsContract from '@mode/charts/contract';
import { ChartDataTypes, ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { HashKey, deepCopy, safeStringToInteger, safeStringToNumber } from '@mode/shared/util-js';
import { CompiledField, Switch } from '@mode/shared/util-switch';
import { getBasicOuterFields } from './chart-encoding-helpers';
import { lookupXField, lookupYField } from './helpers/chart-field';
import { makeSeriesNameFormatter } from './helpers/chart-format';
import { canSubtotalEncFieldChannels, dropLastTotalableField } from './total-helpers';

export type RenderDataModel = ChartsContract.RenderDataModel;
export type SchemaModel = ChartsContract.SchemaModel;
export type TotalSeriesData = ChartsContract.TotalSeriesData;
export type NestedSortDomain = ChartsContract.NestedSortDomain;
export type SortDomainData<R extends DataRole> = ChartsContract.SortDomainData<R>;

export class SwitchSchemaModel implements SchemaModel {
  protected model: FlamingoTypes.ModeModel;
  protected vegasModel: VegasModel;

  static make(model: FlamingoTypes.ModeModel, viewName: string, ..._: any[]) {
    if (model != null && viewName) {
      const view = Switch.Views.find(model, viewName);
      if (view != null) {
        return new SwitchSchemaModel(view);
      }
    }
  }

  constructor(protected view: FlamingoTypes.ModeView) {
    if (view == null) {
      throw new Error('view is required');
    }

    this.model = Switch.Models.makeFromView(view.name, view);
    this.vegasModel = Switch.Compat.makeVegasModel(this.view);
  }

  compileEncField(encField: EncField) {
    return CompiledField.fromEncField(this.view, encField);
  }

  compileField(field: FlamingoTypes.ModeField) {
    return CompiledField.fromField(this.view, field);
  }

  /**
   * Creates Field from EncField.
   *
   * @param {encField|undefined} EncField, or undefined
   * @returns {Field|undefined} Field, or undefined
   */
  makeField(encField: EncField) {
    if (encField) {
      switch (encField.formula) {
        case Names.Key:
          return Switch.Fields.names(encField.id);
        case Values.Key:
          return Switch.Fields.values(encField.id);
        default:
          return Switch.field(
            this.model,
            this.view.name,
            encField.formula,
            Switch.Compat.fromVegasVarType(encField.variable.type),
            encField.id,
            encField.frame
          );
      }
    } else {
      return undefined;
    }
  }

  buildField(formula: string, seriesVarType: VariableType, id: string | undefined) {
    return Switch.field(this.model, this.view.name, formula, Switch.Compat.fromVegasVarType(seriesVarType), id);
  }

  buildViewNamesField(id: string) {
    return Switch.Fields.names(id);
  }

  makeColumn(field: ViewFieldTypes | FlamingoTypes.ModeField, name: string): ChartDataTypes.Column {
    let switchField: FlamingoTypes.ModeField | undefined;
    switch (field.type) {
      case Attr.Type.Continuous:
      case Attr.Type.Discrete:
        switchField = field;
        break;
      default:
        switchField = Switch.field(
          this.model,
          this.view.name,
          field.source,
          Switch.Compat.fromVegasVarType(field.varType),
          field.id
        );
    }

    if (switchField == null) {
      throw new Error(`unable to generate field from ${field.source}`);
    }

    const type = convertSwitchDataType(switchField.formula.type);
    return { name, type, role: type, header: name };
  }

  validateFormula(source: string): [boolean, string | null] {
    return Switch.validate(this.model, this.view.name, source);
  }

  validateFilter(encFilter: EncFilterField, allowAggregate = false): [boolean, string | null] {
    const compiled = CompiledField.fromEncField(this.view, encFilter);
    if (compiled.isValid) {
      if (!allowAggregate && compiled.isNonScalarCalc) {
        return [false, 'Aggregate filters are not supported on this visualization type'];
      }

      if (allowAggregate && compiled.isAnalyticCalc) {
        return [false, 'Analytic filters are not yet supported'];
      }

      return [true, null];
    }

    return [false, compiled.errors[0].message];
  }

  makeViewField(field: FlamingoTypes.ModeField) {
    const vegasField = Switch.Compat.makeField(this.vegasModel, field);
    if (vegasField.type === FieldType.VIEWCALC) {
      return vegasField;
    }
  }
}

export class SwitchRenderModel extends SwitchSchemaModel implements RenderDataModel {
  static override make(
    model: FlamingoTypes.ModeModel,
    viewName: string,
    plan: FlamingoTypes.ModeSelectPlan,
    selectResult: FlamingoTypes.ModeSelectResult
  ) {
    if (model != null && viewName) {
      const view = Switch.Views.find(model, viewName);
      if (view != null) {
        return new SwitchRenderModel(view, plan, selectResult);
      }
    }
  }

  private constructor(
    view: FlamingoTypes.ModeView,
    private plan: FlamingoTypes.ModeSelectPlan,
    private selectResult: FlamingoTypes.ModeSelectResult
  ) {
    super(view);
  }

  getSeriesData(series: string): readonly Readonly<QueryResult.Item>[] {
    if (SelectPlan.isPivot(this.plan)) {
      const result = deepCopy(PivotResult.get(this.plan, this.selectResult, series));
      if (result?.type === QueryResult.Type.Inline) {
        if (series !== '*') {
          const col = result.columns.find((c) => c.alias === series);
          // force measure values into floats for now
          if (col != null && col.source === Values.Key) {
            for (const row of result.items) {
              row[series] = safeStringToNumber(row[series], row[series]);
            }
          }
        } else {
          // force cast all integers
          for (const col of result.columns) {
            if (col.type.name === DataType.Name.Integer) {
              for (const row of result.items) {
                row[col.alias] = safeStringToInteger(row[col.alias], row[col.alias]);
              }
            }
          }
        }

        return result.items;
      }
    }

    return [];
  }

  private _totalSeriesDataCache = new Map<string, Readonly<TotalSeriesData>>();
  getTotalSeriesData(series: string): Readonly<TotalSeriesData> {
    const cached = this._totalSeriesDataCache.get(series);
    if (cached !== undefined) {
      return cached;
    }

    let totals: TotalSeriesData = { series };
    if (!SelectPlan.isPivot(this.plan)) {
      return totals;
    }

    for (const key of ['col', 'row', 'all'] as const) {
      const result = this.getResultByQueryName(this.plan, this.selectResult, `gt:${key}:${series}`);
      const items = this.makeResultItems(result, series);
      if (items) {
        // Add items for this.. channel | dir?
        totals = {
          ...totals,
          [key]: items,
        };
      }
    }

    this._totalSeriesDataCache.set(series, totals);
    return totals;
  }

  private _subtotalSeriesDataCache = new Map<string, Readonly<TotalSeriesData>>();
  getSubtotalSeriesData(encoding: Encoding, series: string): Readonly<TotalSeriesData> {
    const cached = this._subtotalSeriesDataCache.get(series);
    if (cached !== undefined) {
      return cached;
    }
    let totals: TotalSeriesData = { series };
    if (!SelectPlan.isPivot(this.plan)) {
      return totals;
    }

    const makeSubtotalsForKey = (
      key: 'col' | 'row' | 'all',
      fixedFields: readonly EncField[],
      droppableFields: readonly EncField[]
    ) => {
      while (canSubtotalEncFieldChannels([droppableFields])) {
        droppableFields = dropLastTotalableField(droppableFields);

        const path = fixedFields
          .concat(droppableFields)
          .flat()
          .filter((f): f is EncField => f != null)
          .map((f) => f.id)
          .join(',');

        const result = this.getResultByQueryName(this.plan, this.selectResult, `st:${series}:${key}:${path}`);
        const items = this.makeResultItems(result, series);
        if (items) {
          // Add items for this.. channel | dir?
          totals = {
            ...totals,
            [key]: (totals[key] ?? []).concat(items),
          };
        }

        const gtResult = this.getResultByQueryName(this.plan, this.selectResult, `st:${series}:${key}-gt:${path}`);
        const gtItems = this.makeResultItems(gtResult, series);
        if (gtItems) {
          // Add items for this.. channel | dir?
          const gtKey = key === 'col' ? 'row' : 'col';
          totals = {
            ...totals,
            [gtKey]: (totals[gtKey] ?? []).concat(gtItems),
          };
        }
      }
    };

    makeSubtotalsForKey('row', [], [...encoding.column, ...encoding.x]);
    makeSubtotalsForKey('col', [], [...encoding.row, ...encoding.y]);

    // 'All' corner subtotals
    let colFields = [...encoding.column, ...encoding.x];
    while (canSubtotalEncFieldChannels([colFields])) {
      colFields = dropLastTotalableField(colFields);

      const rowFields = [...encoding.row, ...encoding.y];
      makeSubtotalsForKey('all', colFields, rowFields);
    }
    this._subtotalSeriesDataCache.set(series, totals);
    return totals;
  }

  private makeResultItems(result: QueryResult<DataRole> | undefined, series: string) {
    if (result?.type === QueryResult.Type.Inline) {
      let items = result.items;

      if (series === '*') {
        const cast = result.columns.filter((c) => c.type.name === DataType.Name.Integer);
        // force cast all integers, if any
        if (cast.length) {
          items = items.map((item) =>
            cast.reduce(
              (memo, { alias }) =>
                Object.assign(memo, {
                  [alias]: safeStringToInteger(memo[alias], memo[alias]),
                }),
              // Shallow clone item
              {
                ...item,
              }
            )
          );
        }
      } else {
        const col = result.columns.find((c) => c.alias === series);
        // force measure values into floats for now, if any
        if (col != null && col.source === Values.Key) {
          items = items.map((item) =>
            Object.assign({}, item, {
              [series]: safeStringToNumber(item[series], item[series]),
            })
          );
        }
      }
      return items;
    }
  }

  private getResultByQueryName(
    plan: FlamingoTypes.ModeSelectPlan,
    result: FlamingoTypes.ModeSelectResult,
    name: string
  ): QueryResult<FlamingoTypes.ModeRole> | undefined {
    const query = SelectPlan.Queries.getByName(plan, name);
    if (query) {
      return SelectResult.get(result, query);
    }
  }

  isEmpty() {
    if (SelectPlan.isPivot(this.plan)) {
      // Assumes we're only putting one plan in a pivot result
      const result = this.selectResult.results[0];
      if (result.type === QueryResult.Type.Inline) {
        return result.items.length <= 0;
      }
    } else if (SelectPlan.isRecords(this.plan)) {
      return (RecordsResult.countValue(this.plan, this.selectResult) || 0) <= 0;
    }

    return true;
  }

  getSchema() {
    const result = SelectResult.get(this.selectResult, this.plan.queries[this.plan.queries.length > 1 ? 1 : 0]); // assume first of 2 queries is count
    if (result != null && result.type === QueryResult.Type.Inline) {
      return result.columns.map((column) => {
        let type = convertSwitchDataType(column.type);
        const { name } = column;

        switch (column.type.name) {
          case DataType.Name.Boolean:
            type = { name: ChartDataTypes.Column.Type.Name.Boolean };
            break;
          case DataType.Name.Integer:
            type = { name: ChartDataTypes.Column.Type.Name.Integer };
            break;
          case DataType.Name.Decimal:
            type = {
              name: ChartDataTypes.Column.Type.Name.Decimal,
              scale: column.type.scale,
              precision: column.type.precision,
            };
            break;
          case DataType.Name.Float:
            type = { name: ChartDataTypes.Column.Type.Name.Float };
            break;
          case DataType.Name.Timestamp:
          case DataType.Name.DateTime:
            type = { name: ChartDataTypes.Column.Type.Name.Date };
            break;
          default:
          case DataType.Name.String:
            type = { name: ChartDataTypes.Column.Type.Name.String };
        }

        return { name: column.alias, type, role: type, header: name };
      });
    }

    return []; //TODO: return undefined and handle appropriately downstream
  }

  getSeriesSchema(series: string) {
    if (SelectPlan.isPivot(this.plan)) {
      const result = PivotResult.get(this.plan, this.selectResult, series);

      if (result != null && result.type === QueryResult.Type.Inline) {
        return result.columns.map((column) => {
          const { name } = column;
          let type = convertSwitchDataType(column.type);

          switch (column.type.name) {
            case DataType.Name.Boolean:
              type = { name: ChartDataTypes.Column.Type.Name.Boolean };
              break;
            case DataType.Name.Integer:
              type = { name: ChartDataTypes.Column.Type.Name.Integer };
              break;
            case DataType.Name.Decimal:
              type = {
                name: ChartDataTypes.Column.Type.Name.Decimal,
                scale: column.type.scale,
                precision: column.type.precision,
              };
              break;
            case DataType.Name.Float:
              type = { name: ChartDataTypes.Column.Type.Name.Float };
              break;
            case DataType.Name.Timestamp:
            case DataType.Name.DateTime:
              type = { name: ChartDataTypes.Column.Type.Name.Date };
              break;
            default:
            case DataType.Name.String:
              type = { name: ChartDataTypes.Column.Type.Name.String };
          }

          return { name: column.alias, type, role: type, header: name };
        });
      }
    }

    return []; //TODO: return undefined and handle appropriately downstream
  }

  getColorDomain(
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    seriesId?: string,
    applyFormat = true
  ): (string | null | undefined)[] {
    const { series, properties } = encoding.marks;
    let props = properties;

    if (series.length) {
      const seriesPropIndex = series.findIndex((s: EncSeries) => s.field === seriesId);
      props = seriesPropIndex >= 0 ? series[seriesPropIndex].properties : series[0].properties;
    }
    const colorEncField = props.color.values[0];

    if (colorEncField != null && colorEncField.formula !== Names.Key) {
      const colorFieldId = colorEncField.id;
      if (colorFieldId == null) {
        throw new Error('Color field is missing alias');
      }

      const compiled = this.compileEncField(colorEncField);
      const seriesNameFormatter = applyFormat ? makeSeriesNameFormatter(compiled, format) : undefined;

      const formatDomain = (domainRows: QueryResult.Item[]): Array<string | null> => {
        const domainValues = domainRows.map((row) => row[colorFieldId]).sort(domainSortComparator);
        return applyFormat && seriesNameFormatter ? domainValues.map(seriesNameFormatter) : domainValues;
      };

      switch (this.plan.type) {
        case PlanType.PivotDetail: {
          const result = PivotResult.get(this.plan, this.selectResult, seriesId || this.plan.levels[0].name);
          if (result?.type === QueryResult.Type.Inline) {
            const domainMap = new Set();
            const domain = result.items.filter((row) => {
              if (!domainMap.has(row[colorFieldId])) {
                domainMap.add(row[colorFieldId]);
                return true;
              }

              return false;
            });

            return formatDomain(domain);
          }
          break;
        }
        case PlanType.PivotBasic: {
          const result = PivotResult.get(this.plan, this.selectResult, colorFieldId);
          if (result != null && result.type === QueryResult.Type.Inline) {
            const domainMap = new Set();
            const domain = result.items.filter((row) => {
              if (!domainMap.has(row[colorFieldId])) {
                domainMap.add(row[colorFieldId]);
                return true;
              }

              return false;
            });

            return formatDomain(domain);
          }
        }
      }
    } else if (series.length > 0) {
      // for combo charts
      let encFields: EncField[] = [];
      series.forEach((s) => {
        const encField =
          encoding.marks.channel === 'x' ? lookupXField(encoding, s.field) : lookupYField(encoding, s.field);
        if (encField != null) {
          if (encField.formula === Values.Key) {
            encFields.push(...encoding.value);
          } else {
            encFields.push(encField);
          }
        }
      });

      return encFields.map((f) => this.makeField(f)?.name);
    }

    return [];
  }

  getSortDomain<R extends DataRole>(): SortDomainData<R>;
  getSortDomain<R extends DataRole>(alias: string): DomainResult<R> | null;
  getSortDomain<R extends DataRole>(alias?: string): SortDomainData<R> | DomainResult<R> | null {
    const plan = this.plan;
    if (!SelectPlan.isPivot(plan)) {
      return typeof alias === 'string' ? null : {};
    }

    if (typeof alias === 'string') {
      return DomainResult.get(plan, this.selectResult, alias);
    }

    // Distinct fields from the domain plan
    const parts = plan.parts;
    const aliases = Array.from(
      new Set(
        parts.map((part) => {
          return part.field.alias;
        })
      )
    );

    return aliases.reduce(
      (memo, alias) =>
        Object.assign(memo, {
          [alias]: DomainResult.get(plan, this.selectResult, alias),
        }),
      {}
    );
  }

  private _nestedSortDomainCache = new Map<string, NestedSortDomain | undefined>();
  getNestedSortDomain(encoding: Encoding, alias: string): NestedSortDomain | undefined {
    const cached = this._nestedSortDomainCache.get(alias);
    if (cached !== undefined) {
      return cached;
    }

    const plan = this.plan;
    if (!SelectPlan.isPivot(plan)) {
      return;
    }

    // Bail early if there are 0 query results
    const domainQueryResults = DomainResult.getQueryResults(plan, this.selectResult, alias);
    if (domainQueryResults.length === 0) {
      return;
    }

    const outer = getBasicOuterFields(encoding, alias).map((f) => f.id as string);
    const domain = new Map<number, Map<unknown, number>>();
    const keyFn = HashKey.unkeyed(outer);

    for (const { result } of domainQueryResults) {
      if (result.type === QueryResult.Type.Inline) {
        result.items.forEach((item) => {
          const key = keyFn(item);
          const value = item[alias];
          const indices = domain.get(key) ?? new Map();
          domain.set(key, indices);
          if (!indices.has(value)) {
            indices.set(value, indices.size);
          }
        });
      }
    }

    const result = { alias, outer, keyFn, domain };
    this._nestedSortDomainCache.set(alias, result);
    return result;
  }
}

export function convertSwitchDataType(dataType: DataType): ChartDataTypes.Column.Type {
  switch (dataType.name) {
    case DataType.Name.Boolean:
      return { name: ChartDataTypes.Column.Type.Name.Boolean };
    case DataType.Name.Integer:
      return { name: ChartDataTypes.Column.Type.Name.Integer };
    case DataType.Name.Decimal:
      return {
        name: ChartDataTypes.Column.Type.Name.Decimal,
        scale: dataType.scale,
        precision: dataType.precision,
      };
    case DataType.Name.Float:
      return { name: ChartDataTypes.Column.Type.Name.Float };
    case DataType.Name.Timestamp:
    case DataType.Name.DateTime:
      return { name: ChartDataTypes.Column.Type.Name.Date };
    default:
    case DataType.Name.String:
      return { name: ChartDataTypes.Column.Type.Name.String };
  }
}

// Sort comparator for domain values. Nulls are at the end. Sort number numerically.
// Others (string, boolean) sort lexigraphically.
export function domainSortComparator(a: number | string | boolean | null, b: number | string | boolean | null) {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return 1;
  }
  if (b == null) {
    return -1;
  }
  return typeof a === 'number' && typeof b === 'number' ? a - b : a.toString().localeCompare(b.toString());
}
